import { useTranslation } from "../../i18n/LanguageProvider";
import { format } from "date-fns";
import { de, enUS, sk } from "date-fns/locale";
import {
  Play, Square, Clock, Coffee, MapPin, ChevronDown,
  Navigation, Loader2, Info, Users, X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toLocalHHMM } from "./TimeClockHelpers";

export function DesktopStandardMode({
  currentTime, clockStatus, clockedInAt, projects, selectedProject, setSelectedProject,
  handleClockIn, handleClockOut, handleStartBreak, handleEndBreak,
  getElapsedTime, getWorkingTime, todayEntry, user, gpsEnabled, gpsLoading, gpsPermission,
  onShowGpsInfo, clockMode, setClockMode, groups, selectedGroup, setSelectedGroup,
  handleGroupClockIn, handleGroupClockOut, groupClockLoading, groupClockResult, setGroupClockResult,
  canGroupClock, selectedBreakMinutes, setSelectedBreakMinutes,
  dailyCtx, autoProjectApplied,
}) {
  const { t, lang } = useTranslation();
  const dateLoc = lang === "sk" ? sk : lang === "en" ? enUS : de;
  const plannedIds = (dailyCtx?.planned_projects || []).map(p => p.id);
  const plannedSet = new Set(plannedIds);
  const activeProjects = projects.filter(p => p.is_active !== false && p.status === "aktiv");
  const dropdownProjects = plannedIds.length > 0
    ? activeProjects.filter(p => plannedSet.has(p.id))
    : activeProjects;
  const showProjectDropdown = dropdownProjects.length > 1;
  const singleProject = dropdownProjects.length === 1 ? dropdownProjects[0] : null;
  const hasValidProject = selectedProject || singleProject?.id;
  const clockedInTime = todayEntry?.clock_in_local || (clockedInAt ? format(clockedInAt, "HH:mm") : null);

  const getElapsedWorkTime = () => {
    if (!clockedInAt) return "00:00:00";
    const elapsed = Math.max(0, Math.floor((currentTime - clockedInAt) / 1000));
    const h = Math.floor(elapsed / 3600);
    const m = Math.floor((elapsed % 3600) / 60);
    const s = elapsed % 60;
    return `${h.toString().padStart(2,"0")}:${m.toString().padStart(2,"0")}:${s.toString().padStart(2,"0")}`;
  };

  return (
    <div className="mx-auto px-4 py-6" style={{ maxWidth: 440 }} data-testid="desktop-clock-mode">
      <div className="text-center mb-4">
        <p className="text-gray-500 text-sm">{format(currentTime, "EEEE, dd.MM.yyyy", { locale: dateLoc })}</p>
        <p className="text-[36px] font-bold text-gray-900 font-mono mt-0.5" data-testid="current-time">{format(currentTime, "HH:mm:ss")}</p>
      </div>

      <div className="text-center mb-3">
        {clockStatus === "working" ? (
          <span className="inline-flex items-center gap-1.5 text-green-700 font-medium text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
            {t("clock.eingestempelt_seit")} {clockedInTime}
          </span>
        ) : clockStatus === "break" ? (
          <span className="inline-flex items-center gap-1.5 text-yellow-700 font-medium text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 animate-pulse" />
            {t("clock.pause")} - {t("clock.eingestempelt_seit")} {clockedInTime}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-red-600 font-medium text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            {t("clock.nicht_eingestempelt", "Nicht eingestempelt")}
          </span>
        )}
      </div>

      {clockStatus && (
        <div className="text-center mb-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">{t("m.aktuelle_arbeitszeit")}</p>
          <p className="text-[28px] font-semibold text-green-600 font-mono" data-testid="elapsed-time">{getElapsedWorkTime()}</p>
        </div>
      )}

      {canGroupClock && groups.length > 0 && !clockStatus && (
        <div className="mb-4">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button onClick={() => setClockMode("single")} className={`flex-1 py-2 text-[12px] font-medium rounded-md transition-colors ${clockMode === "single" ? "bg-white shadow text-gray-900" : "text-gray-500"}`} data-testid="clock-mode-single">Einzelperson</button>
            <button onClick={() => setClockMode("group")} className={`flex-1 py-2 text-[12px] font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${clockMode === "group" ? "bg-white shadow text-gray-900" : "text-gray-500"}`} data-testid="clock-mode-group">
              <Users className="w-3.5 h-3.5" /> Sammelstempeln
            </button>
          </div>
        </div>
      )}

      {clockMode === "group" && canGroupClock && !clockStatus && (
        <div className="mb-3 space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block font-medium uppercase tracking-wide">Gruppe</label>
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="h-11 text-sm bg-white" data-testid="clock-group-select"><SelectValue placeholder="Gruppe waehlen..." /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id} className="py-2.5">
                    <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-gray-400" />{g.name} ({g.members?.length || 0} MA)</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedGroup && (() => {
            const grp = groups.find(g => g.id === selectedGroup);
            if (!grp) return null;
            return (
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold mb-2">Mitglieder ({grp.members?.length || 0})</p>
                <div className="space-y-1">
                  {(grp.members || []).map((m) => (
                    <div key={m.id} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[8px] font-semibold text-slate-600">{m.name.split(" ").map(n => n[0]).join("").substring(0, 2)}</div>
                      <span className="text-[12px] text-gray-700">{m.name}</span>
                      {m.id === grp.leader_id && <span className="text-[9px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium">Leiter</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {!clockStatus && <div className="mb-3" />}

      {!clockStatus && (clockMode === "single" || clockMode === "group") && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1 block font-medium uppercase tracking-wide">{t("m.projekt")}</label>
          {showProjectDropdown ? (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="h-11 text-sm bg-white" data-testid="clock-project-select"><SelectValue placeholder={t("m.projekt_waehlen")} /></SelectTrigger>
              <SelectContent>
                {dropdownProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="py-2.5">
                    <span className="flex items-center gap-2"><span className="inline-block w-2 h-2 shrink-0 rounded-[1px]" style={{ backgroundColor: p.color || "#6b7280" }} />{p.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : singleProject ? (
            <div className="h-11 flex items-center px-3 bg-white border border-gray-200 rounded-md text-gray-900 text-sm">
              <MapPin className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" /><span className="truncate">{singleProject.name}</span>
            </div>
          ) : (
            <div className="h-11 flex items-center px-3 bg-gray-100 border border-gray-200 rounded-md text-gray-500 text-sm">{t("m.kein_projekt")}</div>
          )}
          {autoProjectApplied && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-blue-600" data-testid="clock-auto-project-hint">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              <span>Projekt aus Einsatzplanung übernommen</span>
            </div>
          )}
          {dailyCtx?.planned_projects?.length > 1 && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-amber-600" data-testid="clock-multi-project-hint">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              <span>Mehrere Projekte für heute eingeplant</span>
            </div>
          )}
          {dailyCtx?.accommodation && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-purple-600" data-testid="clock-accommodation-hint">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
              <span>Übernachtung erkannt: {dailyCtx.accommodation.name}</span>
            </div>
          )}
        </div>
      )}

      {clockStatus && selectedProject && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1 block font-medium uppercase tracking-wide">{t("m.projekt")}</label>
          <div className="h-11 flex items-center px-3 bg-gray-100 border border-gray-200 rounded-md text-gray-600 text-sm">
            <MapPin className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
            <span className="truncate">{projects.find(p => p.id === selectedProject)?.name || t("m.projekt")}</span>
          </div>
        </div>
      )}

      {user?.auto_pause_minutes > 0 && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="auto-pause-indicator">
          <Coffee className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-700 font-medium">
            {(user.auto_pause_min_hours ?? 0) > 0
              ? t("m.auto_pause_detail").replace("{min}", user.auto_pause_minutes).replace("{hours}", user.auto_pause_min_hours)
              : t("m.auto_pause_detail_no_min").replace("{min}", user.auto_pause_minutes)}
          </p>
        </div>
      )}

      {clockMode === "group" && canGroupClock && selectedGroup && !clockStatus && (user?.role === "admin" || user?.role === "projektleiter") && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1 block font-medium uppercase tracking-wide">Gruppenpause</label>
          <div className="flex gap-1.5">
            {[0, 30, 60].map((mins) => (
              <button key={mins} onClick={() => setSelectedBreakMinutes(mins)} className={`flex-1 py-2 text-[12px] font-semibold rounded-lg border-2 transition-colors ${selectedBreakMinutes === mins ? "border-slate-800 bg-slate-800 text-white" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`} data-testid={`group-pause-${mins}`}>{mins} Min.</button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Wird auf alle Gruppenmitglieder angewendet</p>
        </div>
      )}

      {gpsEnabled && (
        gpsPermission === "granted" ? (
          <div className="flex items-center gap-1.5 px-3 py-2 bg-green-50 border border-green-200 rounded-lg mb-3" data-testid="gps-active-banner-desktop">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-green-700 font-medium flex-1">GPS aktiv</span>
            <button onClick={onShowGpsInfo} className="text-green-600 hover:text-green-800 p-0.5" data-testid="gps-info-btn-desktop"><Info className="w-4 h-4" /></button>
            {gpsLoading && <Loader2 className="w-3.5 h-3.5 text-green-600 animate-spin" />}
          </div>
        ) : gpsPermission === "denied" ? (
          <div className="flex items-center gap-1.5 px-3 py-2 bg-red-50 border border-red-200 rounded-lg mb-3" data-testid="gps-denied-banner-desktop">
            <Navigation className="w-3.5 h-3.5 text-red-600 shrink-0" />
            <span className="text-xs text-red-700 font-medium flex-1">GPS blockiert</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg mb-3" data-testid="gps-required-banner-desktop">
            <Navigation className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span className="text-xs text-amber-700 font-medium flex-1">GPS Tracking erforderlich</span>
          </div>
        )
      )}

      <div className="space-y-2">
        {!clockStatus ? (
          clockMode === "group" && canGroupClock ? (
            <>
              {(!selectedProject || !selectedGroup) && (
                <p className="text-center text-orange-600 text-sm mb-1 font-medium">{!selectedGroup ? "Bitte Gruppe waehlen" : "Bitte Projekt waehlen"}</p>
              )}
              <button onClick={handleGroupClockIn} disabled={!selectedProject || !selectedGroup || groupClockLoading} className="w-full h-14 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xl font-bold uppercase tracking-wider rounded-xl flex items-center justify-center gap-2" data-testid="group-clock-in-btn">
                {groupClockLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Stempel...</> : <><Users className="w-5 h-5" /> Gruppe einstempeln</>}
              </button>
              <button onClick={handleGroupClockOut} disabled={!selectedGroup || groupClockLoading} className="w-full h-12 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-base font-bold uppercase tracking-wider rounded-xl flex items-center justify-center gap-2" data-testid="group-clock-out-btn">
                {groupClockLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Stempel...</> : <><Users className="w-5 h-5" /> Gruppe ausstempeln</>}
              </button>
            </>
          ) : (
            <>
              {!hasValidProject && <p className="text-center text-orange-600 text-sm mb-1 font-medium">{t("m.bitte_projekt")}</p>}
              <button
                onClick={() => { if (singleProject && !selectedProject) { setSelectedProject(singleProject.id); setTimeout(() => handleClockIn(), 50); } else { handleClockIn(); } }}
                disabled={!hasValidProject || gpsLoading}
                className="w-full h-14 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xl font-bold uppercase tracking-wider rounded-xl flex items-center justify-center gap-2"
                data-testid="clock-in-btn"
              >
                {gpsLoading ? <><Loader2 className="w-5 h-5 animate-spin" />{t("clock.standort")}</> : t("m.einstempeln")}
              </button>
            </>
          )
        ) : (
          <>
            {clockStatus === "working" ? (
              <button onClick={handleStartBreak} className="w-full h-11 border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-50 text-sm font-semibold rounded-xl" data-testid="start-break-btn">{t("clock.pause_start")}</button>
            ) : (
              <button onClick={handleEndBreak} className="w-full h-11 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold rounded-xl" data-testid="end-break-btn">{t("clock.pause_beenden")}</button>
            )}
            <button onClick={handleClockOut} disabled={gpsLoading} className="w-full h-14 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-60 text-white text-xl font-bold uppercase tracking-wider rounded-xl flex items-center justify-center gap-2" data-testid="clock-out-btn">
              {gpsLoading ? <><Loader2 className="w-5 h-5 animate-spin" />{t("clock.standort")}</> : t("m.ausstempeln")}
            </button>
          </>
        )}
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">{user?.name}</p>

      {groupClockResult && (
        <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4" data-testid="group-clock-result">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sammelstempel-Ergebnis</h4>
            <button onClick={() => setGroupClockResult(null)} className="p-0.5 hover:bg-gray-100 rounded"><X className="w-3.5 h-3.5 text-gray-400" /></button>
          </div>
          <p className="text-sm font-medium text-gray-700 mb-2">{groupClockResult.group_name} {groupClockResult.project_name ? `\u2192 ${groupClockResult.project_name}` : ""}</p>
          {groupClockResult.group_break_minutes != null && (
            <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-blue-50 rounded text-[11px] text-blue-700 font-medium" data-testid="group-pause-active-hint">
              <Coffee className="w-3 h-3" /> Gruppenpause aktiv: {groupClockResult.group_break_minutes} Min.
            </div>
          )}
          {groupClockResult.clocked_in?.length > 0 && (
            <div className="mb-2">
              <p className="text-[11px] text-green-600 font-semibold mb-1">Eingestempelt ({groupClockResult.clocked_in.length})</p>
              {groupClockResult.clocked_in.map((m) => (
                <p key={m.id} className="text-[12px] text-gray-600 flex items-center gap-1.5 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> {m.name}
                  {m.break_minutes != null && <span className="text-gray-400 text-[10px] font-mono ml-auto">Pause: {m.break_minutes} Min.</span>}
                </p>
              ))}
            </div>
          )}
          {groupClockResult.clocked_out?.length > 0 && (
            <div className="mb-2">
              <p className="text-[11px] text-red-600 font-semibold mb-1">Ausgestempelt ({groupClockResult.clocked_out.length})</p>
              {groupClockResult.clocked_out.map((m) => (
                <p key={m.id} className="text-[12px] text-gray-600 flex items-center gap-1.5 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {m.name}
                  <span className="ml-auto flex items-center gap-2">
                    {m.break_minutes != null && <span className="text-gray-400 text-[10px] font-mono">Pause: {m.break_minutes} Min.</span>}
                    {m.net_work_time && <span className="text-gray-500 font-mono text-[10px]">{m.net_work_time}</span>}
                  </span>
                </p>
              ))}
            </div>
          )}
          {groupClockResult.skipped?.length > 0 && (
            <div>
              <p className="text-[11px] text-amber-600 font-semibold mb-1">Uebersprungen ({groupClockResult.skipped.length})</p>
              {groupClockResult.skipped.map((m) => (
                <p key={m.id} className="text-[12px] text-gray-500 flex items-center gap-1.5 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> {m.name} <span className="text-gray-400 text-[10px]">({m.reason})</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {todayEntry && todayEntry.clock_out && (
        <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4" data-testid="desktop-today-summary">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t("clock.heutige_arbeitszeit")}</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">{t("clock.projekt")}</span><span className="font-medium text-gray-900">{todayEntry.project_name || "\u2013"}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">{t("clock.einstempeln")}</span><span className="font-mono">{toLocalHHMM(todayEntry.clock_in, todayEntry.clock_in_local)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">{t("clock.ausstempeln")}</span><span className="font-mono">{toLocalHHMM(todayEntry.clock_out, todayEntry.clock_out_local)}</span></div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("clock.pause")}</span>
              <span className="font-mono">{Math.floor((todayEntry.total_break_seconds || 0) / 60)} min{todayEntry.auto_pause_applied && <span className="text-amber-600 ml-1 text-[10px]">{t("clock.auto")}</span>}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-100"><span className="text-gray-700 font-medium">{t("clock.netto")}</span><span className="font-mono font-semibold text-green-700">{todayEntry.net_work_time}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
